// Pure state logic for the gateway "link with phone number" (pairing-code) connect flow (FE #302).
//
// The whole flow is DRIVEN BY THE SESSION DOCUMENT: the FE PATCHes the session with
// `{ linkMethod: 'pairing_code', phoneNumber }`; the gateway pod's poll loop then drives
// `status` through awaiting_phone → pairing_code (populating `session.pairingCode`, an 8-digit
// XXXX-XXXX code) → connected, and stamps `pairingCodeError` on a bad number / expiry. This
// module maps that live session state onto the sub-flow panel the dialog should render — kept
// pure so it is unit-tested without a store or a rendered tree.

export type PairingStep = 'enter_phone' | 'requesting' | 'show_code' | 'connected' | 'error';

export interface PairingSessionView {
  status?: string | null;
  /** The formatted 8-digit code (e.g. "1234-5678") the pod writes once WhatsApp returns it. */
  pairingCode?: string;
  /** Set by the pod when the number is rejected / the code request fails. */
  pairingCodeError?: string;
  linkMethod?: string;
}

/**
 * Which panel the phone-linking sub-flow should show, given the live session and whether the
 * user has already submitted a number this session.
 *
 * Priority (highest first): connected (terminal success) → a populated code → a fresh error →
 * awaiting_phone (pod wants a number it doesn't have) → requesting (submitted, pod still working)
 * → enter_phone (initial).
 */
export function derivePairingStep(
  session: PairingSessionView | undefined | null,
  phoneSubmitted: boolean,
): PairingStep {
  const status = session?.status ?? '';

  // Terminal success wins over any lingering code/error.
  if (status === 'connected') return 'connected';

  // A populated code is the whole point — show it as soon as it exists, even across a remount
  // that lost the local submit flag, and even if a stale error is still on the doc.
  if (session?.pairingCode) return 'show_code';

  // The pod explicitly wants a phone number it doesn't have → send the user back to the input
  // (their number never took), rather than spinning on "requesting" forever.
  if (status === 'awaiting_phone') return 'enter_phone';

  // A rejected number / expired-code failure.
  if (session?.pairingCodeError) return 'error';

  // Submitted and the pod is booting / requesting the code from WhatsApp.
  if (phoneSubmitted) return 'requesting';

  return 'enter_phone';
}

export type QrStep = 'starting' | 'scan' | 'reconnecting' | 'linking' | 'connected';

/**
 * Which panel the QR sub-flow should show.
 *
 * `qrSeen` = a QR was on screen at some point while this dialog was open. Once it has been, the
 * pod leaving `qrcode` is either a scan or a QR-set expiry — both walk qrcode →
 * stream_disconnected → waiting_for_whatsapp_page, and nothing on the session separates them
 * until WhatsApp opens. So that gap is 'reconnecting' (neutral). It used to fall through to
 * "generating the QR code…", which is what made a successful scan look stuck. `syncing` only
 * follows a real link, so it is the first point we can honestly say "Scanned!".
 */
export function deriveQrStep(
  session: PairingSessionView | undefined | null,
  qrCode: string,
  qrSeen: boolean,
): QrStep {
  const status = session?.status ?? '';
  if (status === 'connected') return 'connected';
  if (status === 'qrcode' && qrCode) return 'scan';
  if (status === 'syncing') return 'linking';
  if (qrSeen && status !== 'qrcode') return 'reconnecting';
  return 'starting';
}

/** Statuses where the connection is waiting on the USER (scan / enter a code), not on us. */
export function isAwaitingUserAction(status: string | null | undefined): boolean {
  return status === 'qrcode' || status === 'awaiting_phone' || status === 'pairing_code' || status === 'unpaired';
}

/**
 * Whether a gateway row should offer the "Connect / link" affordance. Anything that is not
 * actively connected can be (re)linked — including a never-scanned `unpaired` gateway, which is
 * exactly the mobile dead-end #302 fixes (an unpaired row previously had no connect entry at all).
 */
export function isConnectableStatus(status: string | null | undefined): boolean {
  return status !== 'connected';
}

/** Whether a row shows its own Connect / Scan QR button: any non-legacy gateway that is not connected, except mid-restart. */
export function showRowConnect(engine: { type?: string; isLegacy?: boolean; status?: string | null }, restarting: boolean): boolean {
  return engine.type === 'gateway' && !engine.isLegacy && isConnectableStatus(engine.status) && !restarting;
}

export type RestartOutcome = 'pending' | 'reconnected' | 'needs_scan';

export interface RestartSnapshot {
  connectedAt?: string | number | Date | null;
  qrCode?: string;
}

/**
 * Whether a restart the user just triggered has finished, judged against a snapshot taken at the
 * click. Compares VALUES the pod writes (a new connectedAt / a new QR), never timestamps against the
 * browser clock, so client/server clock skew cannot fake or hide the outcome.
 *
 * Why this is needed at all: the displayed status masks blips for 10 minutes after a confirmed
 * connection, so a restart of a healthy connection never visibly left "Connected" — the only sign
 * anything happened was a spinning icon for the length of one request.
 */
export function restartOutcome(
  snapshot: RestartSnapshot,
  session: { status?: string | null; connectedAt?: string | number | Date | null; qrCode?: string } | undefined | null,
): RestartOutcome {
  if (!session) return 'pending';
  const same = (a: unknown, b: unknown) => String(a ?? '') === String(b ?? '');
  if (session.status === 'connected' && session.connectedAt && !same(session.connectedAt, snapshot.connectedAt)) {
    return 'reconnected';
  }
  if (session.status === 'qrcode' && session.qrCode && session.qrCode !== (snapshot.qrCode ?? '')) {
    return 'needs_scan';
  }
  return 'pending';
}

export type StatusHintKey = 'hintScan' | 'hintPairing' | 'hintStarting' | 'hintSyncing' | 'hintDisconnected';

/** The one plain-language line under a row's status: what is happening, or what to do next. */
export function statusHint(status: string | null | undefined): StatusHintKey | null {
  switch (status) {
    case 'qrcode':
      return 'hintScan';
    case 'awaiting_phone':
    case 'pairing_code':
      return 'hintPairing';
    case 'initializing':
    case 'browser_started':
    case 'navigating':
    case 'waiting_for_whatsapp_page':
    case 'PAIRING':
      return 'hintStarting';
    case 'syncing':
      return 'hintSyncing';
    case 'disconnected':
    case 'error':
    case 'logout':
    case 'unpaired':
      return 'hintDisconnected';
    default:
      return null;
  }
}
